import { AddressDataQualityType } from '@/api/quality';
import { addressDataQualityQueryOptions, useAcceptAddressDataQualityMutation } from '@/api/qualityQueryOptions';
import { ApiErrorComponent } from '@/components/ApiErrorComponent';
import { useColumnState } from '@/components/hooks/useColumnState';
import useDetailCrudEvents from '@/components/hooks/useDetailCrudEvents';
import useGridPageSize from '@/components/hooks/useGridPageSize';
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel';
import useResponsive from '@/components/hooks/useResponsive';
import LoadProgress from '@/components/LoadProgress';
import { OverviewGridToolbar } from '@/components/mui-x/datagrid/OverviewGridToolbar';
import CheckIcon from '@mui/icons-material/Check';
import { Box } from '@mui/material';
import {
    GridActionsCellItem,
    GridColDef,
    GridColumnVisibilityModel,
    GridRowId
} from '@mui/x-data-grid';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { t } from 'i18next';
import { enqueueSnackbar } from 'notistack';
import React from 'react';

export const Route = createFileRoute('/_auth/qualityAddress')({
    component: QualityCheckComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function QualityCheckComponent() {
    const addressDataQualityQuery = useSuspenseQuery(
        addressDataQualityQueryOptions(),
    )
    const [addressDataQuality, setAddressDataQuality] = React.useState(addressDataQualityQuery.data)

    const organizations = [
        ...new Set(addressDataQuality.map((qua) => qua.organization.name || '')),
    ].sort()

    const { xsmall, small, medium, large, xlarge } = useResponsive()

    const { onError } =
        useDetailCrudEvents<AddressDataQualityType>('addressDataQuality')

    const onCreated = (data: AddressDataQualityType) => {
        data
        enqueueSnackbar(t('action.accepted', { entity: t('entity.addressDataQuality') }), { variant: 'success' });
    }
    const acceptAddressDataQualityMutation = useAcceptAddressDataQualityMutation(
        onCreated,
        onError,
    )

    const handleAcceptClick = (_id: GridRowId, row: any) => () => {
        acceptAddressDataQualityMutation.mutate({ ...row })
        setAddressDataQuality((prev) => {
            return prev?.filter((item) => item.uuid !== row.uuid)
        }
        )
    }

    const columns: GridColDef[] = []
    if (xsmall || small || medium) {
        columns.push({
            field: 'member',
            headerName: 'Member',
            valueGetter: (_value, row) =>
                `${row.scoutId} - ${row.firstname} ${row.lastname}`,
            renderCell: (params) => {
                return (
                    <span>
                        <Link to='/member/$memberUuid/main' params={{ memberUuid: params.row.member.uuid }}>
                            {params.row.scoutId} - {params.row.firstname} {params.row.lastname}
                        </Link>
                    </span>
                )
            },
        })
    } else {
        columns.push(
            {
                field: 'scoutId',
                headerName: 'Scout-ID',
                valueGetter: (_value, row) => `${row.scoutId}`,
                renderCell: (params) => {
                    return (
                        <span>
                            <Link to='/member/$memberUuid/main' params={{ memberUuid: params.row.member.uuid }}>
                                {params.row.scoutId}
                            </Link>
                        </span>
                    )
                },
            },
            {
                field: 'firstname',
                headerName: 'Vorname',
                valueGetter: (_value, row) => `${row.firstname}`,
            },
            {
                field: 'lastname',
                headerName: 'Nachname',
                valueGetter: (_value, row) => `${row.lastname}`,
            },
        )
    }
    columns.push(
        {
            field: 'organization',
            headerName: t('property.membership.organization'),
            type: 'singleSelect',
            valueOptions: organizations,
            valueGetter: (_value, row) => `${row.organization.name}`,
        },
        {
            field: 'address',
            headerName: t('property.member.address'),
            valueGetter: (_value, row) => `${row.street}, ${row.postcode} ${row.city}`,
        },
        {
            field: 'suggest',
            headerName: t('property.quality.suggest'),
            valueGetter: (_value, row) => `${row.suggest || ''}`,
            renderCell: (params) => {
                if (params.row.suggest) {
                    return (
                        <span>{params.row.suggest}</span>
                    )
                } else {
                    return (
                        <span style={{ color: 'grey' }}>{t('property.quality.noSuggestion')}</span>
                    )
                }

            },
        },

    )
    columns.push(
        {
            field: 'acceptAction',
            type: 'actions',
            headerName: t('action.accept'),
            width: 50,
            getActions: ({ id, row }: { id: GridRowId, row: any }) => {
                if (row.suggest) {
                    return [
                        <GridActionsCellItem
                            icon={<CheckIcon />}
                            label='Clone'
                            key={`clone-${id}`}
                            onClick={handleAcceptClick(id, row)}
                        />
                    ]
                } else {
                    return []
                }
            },
        }
    )

    const initialVisibilityModel = {
        member: xsmall || small || medium,
        firstname: large || xlarge,
        lastname: large || xlarge,
        scoutId: large || xlarge,
        organizations: large || xlarge,
        message: true,
        messageType: large || xlarge,
    } as GridColumnVisibilityModel

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('qualityAddress', initialVisibilityModel)
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('qualityAddress', 35)
    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('qualityAddress', 35));

    const apiRef = useGridApiRef()
    const columnsState = useColumnState('qualityAddress', apiRef, columns)

    const refreshData = () => {
        addressDataQualityQuery.refetch()
    }

    if (
        addressDataQualityQuery.isLoading ||
        addressDataQualityQuery.isFetching ||
        addressDataQualityQuery.isPending ||
        !addressDataQuality
    ) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <DataGridPremium
                columns={columnsState.columns}
                onColumnWidthChange={columnsState.onColumnWidthChange}
                onColumnOrderChange={columnsState.onColumnOrderChange}
                rows={addressDataQuality}
                getRowId={(row) => {
                    return row.uuid || ''
                }}
                pagination
                pageSizeOptions={pageSizeOptions}
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => {
                    setPaginationModel(model)
                    gridPageSize.handlePaginationModelChange('qualityAddress', model)
                }}
                columnVisibilityModel={visibilityModel}
                onColumnVisibilityModelChange={handleVisibilityModelChange}
                slots={{ toolbar: (props) => { return <OverviewGridToolbar {...props} scope="qualityAddress" refresh={refreshData} /> } }}
                slotProps={{
                    toolbar: {
                        csvOptions: {
                            disableToolbarButton: true
                        },
                        printOptions: {
                            disableToolbarButton: true
                        }
                    }
                }}
                density='compact'
                hideFooter={true}
                disableDensitySelector
                disableAggregation
                disableColumnPinning
                disableColumnReorder
                disableRowGrouping
                disableRowSelectionOnClick
                showToolbar />
        </Box>
    );
}
